import styled, { css } from "styled-components";
import { tokens } from "../../../../../design/tokens";

/* ---- Casca do diálogo -------------------------------------------------- */

/* Cabeçalho e rodapé fixos; só o corpo rola. Assim "Salvar" nunca some
   abaixo da dobra, por mais longo que o formulário fique. */
export const Dialog = styled.article`
  display: flex;
  flex-direction: column;
  width: min(1040px, calc(100% - 32px));
  max-height: calc(100vh - 48px);
  margin: 24px auto;

  background: ${tokens.color.cardCharcoal};
  border: 1px solid ${tokens.color.railGraphite};
  border-radius: ${tokens.radius.md};
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.55);
  overflow: hidden;
`;

export const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${tokens.space.lg};
  padding: 20px 24px 16px;
  border-bottom: 1px solid ${tokens.color.railGraphite};
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.space.xs};
  min-width: 0;
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
  color: ${tokens.color.textWhite};
`;

export const Meta = styled.p`
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${tokens.space.sm};
  font-size: ${tokens.font.caption.size};
  color: ${tokens.color.textAsh};

  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    color: ${tokens.color.textMist};
    overflow-wrap: anywhere;
  }
`;

export const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;

  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 32px;
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 24px;
    padding: 20px 16px;
  }
`;

export const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${tokens.space.lg};
  flex-wrap: wrap;
  padding: 14px 24px;
  border-top: 1px solid ${tokens.color.railGraphite};
  background: ${tokens.color.cardCharcoal};

  @media (max-width: 860px) {
    padding: 12px 16px;
  }
`;

export const FooterStatus = styled.span<{ dirty: boolean }>`
  font-size: ${tokens.font.caption.size};
  color: ${({ dirty }) => (dirty ? tokens.color.marqueeGold : tokens.color.textAsh)};

  @media (max-width: 860px) {
    display: none;
  }
`;

export const FooterActions = styled.div`
  display: flex;
  gap: ${tokens.space.sm};
  margin-left: auto;

  button {
    min-width: 120px;
  }

  @media (max-width: 860px) {
    width: 100%;

    button {
      flex: 1;
      min-width: 0;
    }
  }
`;

/* ---- Coluna de capas --------------------------------------------------- */

export const Covers = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 20px;

  /* Em tela estreita o formulário vem primeiro; as capas descem. */
  @media (max-width: 860px) {
    order: 2;
    padding-top: 24px;
    border-top: 1px solid ${tokens.color.railGraphite};
  }
`;

export const Cover = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.space.sm};
`;

export const CoverHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  label {
    font-size: ${tokens.font.label.size};
    font-weight: ${tokens.font.label.weight};
    color: ${tokens.color.textAsh};

    span {
      color: ${tokens.color.errorRed};
    }
  }
`;

export const CoverFrame = styled.div<{ shape: "horizontal" | "vertical" }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ shape }) => (shape === "vertical" ? "2 / 3" : "16 / 9")};
  ${({ shape }) =>
    shape === "vertical" &&
    css`
      max-width: 160px;

      @media (max-width: 860px) {
        max-width: 120px;
      }
    `}
  border-radius: ${tokens.radius.sm};
  background: ${tokens.color.stageBlack};
  border: 1px solid ${tokens.color.railGraphite};
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const CoverEmpty = styled.div<{ warn: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: ${tokens.space.sm};
  text-align: center;
  font-size: ${tokens.font.caption.size};
  color: ${({ warn }) => (warn ? tokens.color.marqueeGold : tokens.color.textAsh)};
`;

export const CoverActions = styled.div`
  display: flex;
  gap: ${tokens.space.sm};
`;

export const CoverButton = styled.button<{ quiet?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 0 12px;
  border-radius: ${tokens.radius.sm};
  font-size: ${tokens.font.caption.size};
  font-weight: 600;
  cursor: pointer;
  transition: background-color ${tokens.motion.quick}, color ${tokens.motion.quick};

  border: 1px solid ${({ quiet }) => (quiet ? "transparent" : tokens.color.railGraphite)};
  background: ${({ quiet }) => (quiet ? "transparent" : tokens.color.stageBlack)};
  color: ${({ quiet }) => (quiet ? tokens.color.textAsh : tokens.color.textMist)};

  &:hover:not(:disabled) {
    color: ${tokens.color.marqueeGold};
  }

  &:focus-visible {
    outline: 2px solid ${tokens.color.focusAmber};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: progress;
  }
`;

export const CoverHint = styled.span<{ tone?: "gold" | "error" }>`
  font-size: ${tokens.font.caption.size};
  color: ${({ tone }) =>
    tone === "gold" ? tokens.color.marqueeGold : tone === "error" ? "#ff7b7b" : tokens.color.textAsh};
`;

/* ---- Coluna do formulário ---------------------------------------------- */

export const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  min-width: 0;
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${tokens.space.lg};
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${tokens.color.textWhite};
`;

export const SectionNote = styled.p`
  margin: -8px 0 0;
  font-size: ${tokens.font.caption.size};
  line-height: 1.5;
  color: ${tokens.color.textAsh};
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${tokens.space.lg} 20px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

/* Dropdown do admin já traz o próprio <label>; isto só o encaixa no grid. */
export const DropdownSlot = styled.div<{ full?: boolean }>`
  min-width: 0;
  ${({ full }) =>
    full &&
    css`
      grid-column: 1 / -1;
    `}

  > div > label {
    display: block;
    margin-bottom: 6px;
    font-size: ${tokens.font.label.size};
    font-weight: ${tokens.font.label.weight};
    color: ${tokens.color.textAsh};
  }
`;

export const SlotHint = styled.span<{ tone?: "error" }>`
  display: block;
  margin-top: 6px;
  font-size: ${tokens.font.caption.size};
  color: ${({ tone }) => (tone === "error" ? "#ff7b7b" : tokens.color.textAsh)};
`;

/* Interruptor acessível: checkbox nativo com role="switch". */
export const Switch = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 40px;
  cursor: pointer;
  font-size: ${tokens.font.body.size};
  color: ${tokens.color.textMist};

  input {
    appearance: none;
    flex-shrink: 0;
    position: relative;
    width: 36px;
    height: 20px;
    margin: 0;
    border-radius: 999px;
    background: ${tokens.color.railGraphite};
    border: 1px solid ${tokens.color.fieldSlate};
    cursor: pointer;
    transition: background-color ${tokens.motion.quick};

    &::after {
      content: "";
      position: absolute;
      top: 2px;
      left: 2px;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: ${tokens.color.textMist};
      transition: transform ${tokens.motion.quick} ease-out;
    }

    &:checked {
      background: ${tokens.color.marqueeGold};
      border-color: ${tokens.color.marqueeGold};

      &::after {
        transform: translateX(16px);
        background: ${tokens.color.inkCharcoal};
      }
    }

    &:focus-visible {
      outline: 2px solid ${tokens.color.focusAmber};
      outline-offset: 2px;
    }
  }

  small {
    display: block;
    font-size: ${tokens.font.caption.size};
    color: ${tokens.color.textAsh};
  }
`;

export const Callout = styled.p<{ tone: "warn" | "error" }>`
  margin: 0;
  padding: 10px 12px;
  border-radius: ${tokens.radius.sm};
  font-size: ${tokens.font.caption.size};
  line-height: 1.5;
  ${({ tone }) =>
    tone === "warn"
      ? css`
          color: ${tokens.color.marqueeGold};
          background: rgba(254, 212, 74, 0.08);
          border: 1px solid rgba(254, 212, 74, 0.25);
        `
      : css`
          color: #ff9b9b;
          background: rgba(197, 48, 48, 0.12);
          border: 1px solid rgba(197, 48, 48, 0.4);
        `}
`;

export const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${tokens.space.lg};
  padding: 48px 24px;
  text-align: center;
`;
